import {
	mono,
	monoFont,
	monoForm,
	monoShared,
} from '@components/systems/monolith/monolith.tokens'

/**
 * 결제 화면 공용 스타일 — Monolith(2안).
 *
 * `/payment` 한 장과 그 안의 블록 7개(Items · Customer · Delivery · Card · Total · PayPal ·
 * Pay now)가 **이 객체 하나만** 쓴다. 페이지 루트에 스프레드하면 클래스가 자식 블록까지
 * 그대로 내려가므로, 블록 컴포넌트들은 자기 css 를 들고 있지 않고 클래스 이름만 쓴다 —
 * 값이 갈릴 자리를 만들지 않으려는 것이다.
 *
 * 문법은 DESIGN_SYSTEM §4·§5 그대로 — 4:8 헤어라인 밴드, 밑줄 입력, 칩, 블랙 CTA 하나.
 * 구 결제 화면의 라임 배경·핑크 에러·radius 10 박스·MUI Select 외형은 전부 걷어냈다(§2).
 */
export const paymentPageStyle = {
	...monoShared,
	...monoForm,
	position: 'relative' as const,
	width: '100%',

	'.mnPayHeader': {
		padding: 'clamp(56px, 7vw, 100px) 0 clamp(32px, 3.6vw, 48px)',

		'.mnPayTitle': {
			fontSize: 'clamp(44px, 6vw, 88px)',
			lineHeight: 1,
			fontWeight: 100,
			letterSpacing: '-0.01em',
			color: mono.ink,
		},
		'.mnPayLede': {
			fontSize: 19,
			lineHeight: 1.6,
			fontWeight: 300,
			color: mono.dim,
			maxWidth: 520,
			marginTop: 'clamp(20px, 2vw, 28px)',
		},
	},

	/** 블록 하나 = 4:8 헤어라인 밴드. 좌측은 키커, 우측이 실제 내용 */
	'.mnPaySection': {
		display: 'grid',
		gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 2fr)',
		gap: 'clamp(24px, 4vw, 56px)',
		alignItems: 'start',
		padding: 'clamp(40px, 4.4vw, 64px) 0',
		borderTop: `1px solid ${mono.line}`,

		'@media (max-width: 900px)': {
			gridTemplateColumns: '1fr',
			gap: 24,
		},
	},

	'.mnPaySectionHead': {
		display: 'flex',
		flexDirection: 'column' as const,
		gap: 12,

		'.mnPaySectionNote': {
			fontSize: 14,
			lineHeight: 1.7,
			fontWeight: 300,
			color: mono.dim,
			maxWidth: 320,
		},
	},

	'.mnPaySectionBody': {
		display: 'flex',
		flexDirection: 'column' as const,
		gap: 32,
		width: '100%',
		maxWidth: 640,
	},

	/** 로딩 — 결제 데이터가 오기 전 */
	'.mnPayLoading': {
		width: 240,
		padding: 'clamp(56px, 7vw, 100px) 0',

		'.mnPayLoadingBar': {
			background: mono.line,
			'& .MuiLinearProgress-bar': { background: mono.ink },
		},
	},

	/** 결제 수단 — 칩 라디오 (DESIGN_SYSTEM §5) */
	'.mnPayChips': {
		display: 'flex',
		flexWrap: 'wrap' as const,
		gap: 10,

		'.mnPayChip': {
			padding: '11px 22px',
			fontFamily: monoFont.sans,
			fontSize: 13,
			fontWeight: 400,
			color: mono.faint,
			background: 'transparent',
			border: `1px solid ${mono.lineSoft}`,
			borderRadius: 999,
			cursor: 'pointer',
			transition: 'color 200ms ease-out, border-color 200ms ease-out',

			'&.on': {
				color: mono.ink,
				borderColor: mono.ink,
				fontWeight: 500,
			},
		},
	},

	/** 네이티브 셀렉트를 밑줄 입력 규격으로 눕힌다 (Contact 와 같은 방식) */
	'.mnPaySelectWrap': {
		position: 'relative' as const,
		width: '100%',

		'.mnPaySelect': {
			appearance: 'none' as const,
			WebkitAppearance: 'none' as const,
			paddingRight: 28,
			cursor: 'pointer',
			color: mono.ink,

			'&[data-empty="true"]': {
				color: mono.faint,
			},
		},

		'.mnPaySelectChevron': {
			position: 'absolute' as const,
			right: 2,
			top: '50%',
			transform: 'translateY(-50%)',
			color: mono.faint,
			pointerEvents: 'none' as const,
		},
	},

	/** 주문 품목 행 */
	'.mnPayItemList': {
		display: 'flex',
		flexDirection: 'column' as const,
		width: '100%',
	},

	'.mnPayItem': {
		display: 'grid',
		gridTemplateColumns: 'minmax(0, 110px) minmax(0, 1fr) auto',
		gap: 22,
		alignItems: 'center',
		padding: '18px 0',
		borderBottom: `1px solid ${mono.line}`,

		'.mnPayItemThumb': {
			width: '100%',
			border: `1px solid ${mono.line}`,
			borderRadius: 6,
			background: mono.surface,
			overflow: 'hidden',

			img: {
				display: 'block',
				width: '100%',
				height: 'auto',
			},
		},

		'.mnPayItemName': {
			display: 'flex',
			flexDirection: 'column' as const,
			gap: 6,

			'.mnPayItemProduct': {
				fontSize: 16,
				fontWeight: 400,
				color: mono.ink,
			},
			'.mnPayItemOption': {
				fontSize: 13,
				fontWeight: 300,
				color: mono.faint,
			},
			'.mnPayItemQty': {
				fontSize: 13,
				fontWeight: 500,
				color: mono.faint,
				fontVariantNumeric: 'tabular-nums' as const,
			},
		},

		'.mnPayItemUsd': {
			fontSize: 16,
			fontWeight: 300,
			color: mono.ink,
			fontVariantNumeric: 'tabular-nums' as const,
			textAlign: 'right' as const,
		},

		'@media (max-width: 600px)': {
			gridTemplateColumns: 'minmax(0, 84px) minmax(0, 1fr)',
			rowGap: 12,

			'.mnPayItemUsd': {
				gridColumn: '2 / 3',
				textAlign: 'left' as const,
			},
		},
	},

	/** 금액 행 — 라벨(좌) · 값(우) */
	'.mnPayCostRow': {
		display: 'flex',
		alignItems: 'baseline',
		justifyContent: 'space-between',
		gap: 24,
		padding: '16px 0',
		borderBottom: `1px solid ${mono.line}`,

		'.mnPayCostLabel': {
			fontSize: 13,
			letterSpacing: '.14em',
			textTransform: 'uppercase' as const,
			fontWeight: 500,
			color: mono.faint,
		},
		'.mnPayCostValue': {
			fontSize: 16,
			fontWeight: 300,
			color: mono.ink,
			fontVariantNumeric: 'tabular-nums' as const,
		},

		'&.total': {
			borderBottom: 0,
			paddingTop: 28,

			'.mnPayCostValue': {
				fontSize: 'clamp(30px, 3vw, 40px)',
				lineHeight: 1,
				fontWeight: 200,
			},
		},
	},

	/** 쿠폰 — 밑줄 입력 + 고스트 버튼 */
	'.mnPayCoupon': {
		display: 'flex',
		alignItems: 'flex-end',
		gap: 16,

		'.mnPayCouponField': {
			flex: '1 1 auto',
		},

		'.mnPayCouponApply': {
			height: 46,
			padding: '0 28px',
			flexShrink: 0,

			'&:disabled': {
				borderColor: mono.lineSoft,
				color: mono.faint,
				cursor: 'default',
			},
		},
	},

	/** 배송 수단 선택 행 */
	'.mnPayShipRow': {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		gap: 20,
		width: '100%',
		padding: '16px 0',
		border: 0,
		borderBottom: `1px solid ${mono.line}`,
		background: 'transparent',
		cursor: 'pointer',
		textAlign: 'left' as const,

		'.mnPayShipName': {
			display: 'flex',
			alignItems: 'center',
			gap: 14,
			fontFamily: monoFont.sans,
			fontSize: 16,
			fontWeight: 300,
			color: mono.dim,
		},

		'.mnPayShipMark': {
			width: 12,
			height: 12,
			borderRadius: 999,
			border: `1px solid ${mono.lineInput}`,
			flexShrink: 0,
		},

		'.mnPayShipUsd': {
			fontFamily: monoFont.sans,
			fontSize: 16,
			fontWeight: 300,
			color: mono.ink,
			fontVariantNumeric: 'tabular-nums' as const,
		},

		'&.on': {
			'.mnPayShipName': { color: mono.ink },
			'.mnPayShipMark': {
				borderColor: mono.ink,
				background: mono.ink,
			},
		},
	},

	/** 마지막 동작 — 페이지의 유일한 블랙 CTA */
	'.mnPayAction': {
		display: 'flex',
		flexDirection: 'column' as const,
		alignItems: 'flex-start',
		gap: 20,
		width: '100%',
		maxWidth: 380,

		'.mnPayCta': {
			width: '100%',
		},
	},

	/** PayPal 버튼은 PayPal 이 iframe 으로 그린다 — 폭만 잡아 준다 */
	'.mnPaypal': {
		width: '100%',
		maxWidth: 380,
		minHeight: 150,
	},

	'.mnPayNote': {
		fontSize: 15,
		lineHeight: 1.7,
		fontWeight: 300,
		color: mono.dim,
	},

	'.mnPayQuiet': {
		fontSize: 16,
		fontWeight: 300,
		color: mono.dim,
	},

	'@media (prefers-reduced-motion: reduce)': {
		'.mnPayChip': { transition: 'none' },
	},
}
